// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SettingsByRange.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * SettingsByRange component
 */
import Constants from 'logic/data/Izenda.SettingsByRangeConstants';
import SettingsByRangeModel from 'logic/models/Izenda.SettingsByRange';
import { observer } from 'mobx-react';
import React, { Component } from 'react';
import { getOr, isFunction, assign, concat, flatMap, get, uniqueId } from 'lodash/fp';
import DropDown from 'share/components/controls/Izenda.DropDown';
import Button from './Izenda.Button';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import localizer from 'share/localization/Izenda.Localizer';
import SettingsByRangeItem from './Izenda.SettingsByRangeItemDraggable';

const DATA_TYPE = Constants.DATA_TYPE;
const VALUE_TYPES = Constants.VALUE_TYPES;

function getDateTimeFormatData(dateTimeFormatDataFn) {
  return isFunction(dateTimeFormatDataFn) ? dateTimeFormatDataFn() : dateTimeFormatDataFn;
}

@observer
export default class SettingsByRange extends Component {
  static propsType = {
    dataType: React.PropTypes.string,
    settingValue: React.PropTypes.object,
    valueProps: React.PropTypes.object,
    haveName: React.PropTypes.bool
  };

  static getDefaultProps = {
    haveName: false
  };

  constructor(props) {
    super(props);
    this.model = new SettingsByRangeModel();
    this.model.setSettingValue(props.settingValue);
    const dateTimeFormatData = getDateTimeFormatData(props.dateTimeFormatData);
    this.model.setValueTypeByDataType(props.defaultValueType, dateTimeFormatData);
    this.initData();
  }

  componentWillReceiveProps(nextProps: any) {
    this.model.setSettingValue(nextProps.settingValue);
    const dateTimeFormatData = getDateTimeFormatData(nextProps.dateTimeFormatData);
    this.model.setValueTypeByDataType(nextProps.defaultValueType, dateTimeFormatData);
  }

  getValue = () => {
    return this.model.getSettingValue();
  };

  beforeClosing = (valManager: any, btnType: string) => {
    const validateMessage = getOr({}, 'validateMessages', this.props);

    this.valManager = valManager;
    if (btnType === 'cancel') {
      return true;
    }
    const model = this.model;
    const haveName = this.props.haveName;

    model.removeEmptySettingValue(haveName);

    if (!model.isExistsUncompleteRangeValue(haveName)) {
      const listNameDup = (haveName && model.checkDuplicateName()) || [];
      const dateTimeFormatData = getDateTimeFormatData(this.props.dateTimeFormatData);
      const settingInvaid = model.checkConstrains(
        this.props.dataType,
        getOr(false, 'settingByGroupBy.RANGE_CYCLE', dateTimeFormatData)
      );
      const listRangeDup = settingInvaid.duplicate;
      const invalidRange = settingInvaid.invalidRange;
      let error = [];

      if (listNameDup.length > 0) {
        error = this.buildNameValidationError(listNameDup);
      }
      if (listRangeDup.length > 0) {
        error = concat(error, this.buildOverlapRangeError(listRangeDup));
      }
      if (invalidRange.length > 0) {
        error = concat(error, this.buildErrorList(invalidRange, validateMessage.INVALID_RANGE));
      }

      if (error.length > 0) {
        valManager.reset();
        valManager.updateErrors(error);
        return Promise.resolve(false);
      }
    }
    return Promise.resolve(true);
  };

  buildNameValidationError = listNameDup => {
    const validateMessage = getOr({}, 'validateMessages', this.props);
    const msg = validateMessage.DUPLICATE_NAME;
    const error = [];

    listNameDup.forEach((item) => {
      error.push({
        key: `${item.id}_name`,
        messages: [msg]
      });
    });
    return error;
  };

  buildErrorList = (listNameDup: any, msg: any) => {
    const selected = this.model.selectedValueType;

    return flatMap((item) => {
      if (selected === 'value') {
        return [
          {
            key: item.id,
            messages: [msg]
          }
        ];
      }
      return [
        {
          key: `${item.id}_from`,
          messages: [msg]
        },
        {
          key: `${item.id}_to`,
          messages: [msg]
        }
      ];
    }, listNameDup);
  };

  buildOverlapRangeError = list => {
    const validateMessage = getOr({}, 'validateMessages', this.props);
    const selected = this.model.selectedValueType;
    const msg = getValidationMessage();

    function getValidationMessage() {
      let msg = '';

      switch (selected) {
        case 'value':
          msg = validateMessage.VALUE;
          break;
        case 'rangeValue':
          msg = validateMessage.RANGE_VALUE;
          break;
        case 'rangePercent':
          msg = validateMessage.RANGE_PERCENT;
          break;
      }
      return msg;
    }

    return this.buildErrorList(list, msg);
  };

  addNewSetting = () => {
    this.valManager && this.valManager.reset();
    this.model.addNewSetting();
  };

  render() {
    const { haveName } = this.props;
    const model = this.model;
    const selectedValueType = model.selectedValueType;
    const selectedSettingsValue = model.settingsValue[selectedValueType] || [];

    return (
      <section className="setting-by-range u_paddingAll">
        {/* header*/}
        {this.renderHeader()}

        {/* grid setting*/}

        {selectedSettingsValue.length > 0 ? this.renderGrid() : null}

        {selectedValueType === 'none' || (selectedValueType === 'value' && haveName) ? null : (
          <Button
            className="u_floatRight"
            label={localizer.getText('SETTING_BY_RANGE_ADD_SETTING')}
            onClick={this.addNewSetting}
          />
        )}
      </section>
    );
  }

  renderHeader() {
    const { props, model } = this;
    const valueProps = getOr({}, 'valueProps', props);

    return (
      <GridLayout className="izenda-SettingByRange-Header">
        <GridLayout.Row>
          <GridLayout.Col cols={5} className="u_controlHeight">
            <span>{valueProps.title}</span>
          </GridLayout.Col>
          <GridLayout.Col cols={7}>
            <DropDown
              options={getOptions()}
              value={model.selectedValueType}
              labelField="label"
              valueField="key"
              onChange={changeValueType.bind(this)}
            />
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    );

    function getOptions() {
      const getValueTypes = (input) => {
        const arr = [];
        for (let key in VALUE_TYPES) {
          if (input.indexOf(key) > -1) {
            arr.push(assign(VALUE_TYPES[key], { key: key }));
          }
        }
        return arr;
      };

      if (props.haveName) {
        if (props.isHiddenValueOption) {
          return getValueTypes(['none', 'rangeValue', 'rangePercent']);
        }
        return getValueTypes(['none', 'value', 'rangeValue', 'rangePercent']);
      }

      switch (props.dataType) {
        case DATA_TYPE.TEXT:
        case DATA_TYPE.BOOLEAN:
          return getValueTypes(['value']);
        case DATA_TYPE.DATETIME:
          var dateTimeFormatData = getDateTimeFormatData(props.dateTimeFormatData);
          return getValueTypes(
            getOr(['value', 'rangeValue'], 'settingByGroupBy.SETTINGS_VALUE_TYPE', dateTimeFormatData)
          );
        case DATA_TYPE.TIME:
          return getValueTypes(['value', 'rangeValue']);
        case DATA_TYPE.MONEY:
        case DATA_TYPE.NUMERIC:
          return getValueTypes(['value', 'rangeValue', 'rangePercent']);
        default:
          return getValueTypes(['value', 'rangeValue', 'rangePercent']);
      }
    }

    function changeValueType(newVal: any, newValDetails: any) {
      model.setValueType(newVal.value);
      this.initData();
    }
  }

  renderGrid() {
    const { props, props: { haveName }, model: { selectedValueType, settingsValue } } = this;
    const selectedSettingsValue = settingsValue[selectedValueType] || [];
    const canMove = selectedValueType !== 'value';

    return (
      <table className="table table-bordered u_fullWidth">
        <thead>{renderGridHeader()}</thead>
        <tbody>
          {selectedSettingsValue.map((item, index) =>
            <SettingsByRangeItem
              {...props}
              itemData={item}
              itemIndex={index}
              valueType={selectedValueType}
              key={item.id}
              canMove={canMove}
              onMove={handleMoveItem}
              onDelete={handleDeleteItem.bind(null, index)}
            />
          )}
        </tbody>
      </table>
    );

    function renderGridHeader() {
      return (
        <tr>
          {canMove ? <th /> : null}
          {haveName ? (
            <th>
              <label>{localizer.getText('COMMON_LABEL_NAME')}</label>
            </th>
          ) : null}
          <th className="izenda-SettingByRange_ValueLabel">
            <label>{localizer.getText('COMMON_LABEL_VALUE')}</label>
          </th>
          <th>
            <label>{get('valueProps.title', props)}</label>
          </th>
          {haveName && selectedValueType === 'value' ? null : <th />}
        </tr>
      );
    }

    function handleDeleteItem(index) {
      selectedSettingsValue.splice(index, 1);
    }

    function handleMoveItem(from, to) {
      if (to < 0) to = 0;
      if (to >= selectedSettingsValue.length) to = selectedSettingsValue.length - 1;
      if (from === to)
        return;
      selectedSettingsValue.splice(to, 0, selectedSettingsValue.splice(from, 1)[0]);
    }
  }

  /* Initialize default data for value settings */
  initData() {
    const haveName = this.props.haveName;
    const model = this.model;
    const props = this.props;
    const selectedValueType = model.selectedValueType;
    const selectedSettingsValue = model.settingsValue[selectedValueType] || [];

    if (haveName && selectedValueType === 'value' && selectedSettingsValue.length === 0) {
      model.addNewSetting({
        name: localizer.getText('COMMON_LABEL_LOW'),
        key: '',
        text: props.minColor || '',
        id: uniqueId('setting_'),
        canDelete: false
      });
      model.addNewSetting({
        name: localizer.getText('COMMON_LABEL_HIGH'),
        key: '',
        text: props.maxColor || '',
        id: uniqueId('setting_'),
        canDelete: false
      });
    }
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.SettingsByRange.js